import { useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { VideoCard } from '@/components/VideoCard';
import type { VideoData } from '@/lib/videoTypes';
import { cn } from '@/lib/utils';

interface VideoGridProps {
  videos: VideoData[];
  isLoading: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  /** Predicate marking videos that have kind 1984 reports (shows a badge). */
  isReported?: (video: VideoData) => boolean;
  /** 'shorts' renders a dense 9:16 grid for vertical videos. */
  variant?: 'default' | 'shorts';
  emptyMessage?: string;
}

const GRID_CLASSES = {
  default: 'grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  shorts: 'grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6',
} as const;

function VideoCardSkeleton({ variant }: { variant: 'default' | 'shorts' }) {
  if (variant === 'shorts') {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="aspect-[9/16] w-full rounded-xl" />
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <Skeleton className="aspect-video w-full rounded-xl" />
      <div className="flex gap-2.5">
        <Skeleton className="h-9 w-9 rounded-full shrink-0 mt-0.5" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-3 w-2/3" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      </div>
    </div>
  );
}

export function VideoGrid({
  videos,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  isReported,
  variant = 'default',
  emptyMessage = 'No videos found. Try different filters or check your relay connections.',
}: VideoGridProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Infinite scroll
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !onLoadMore || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) onLoadMore();
        }
      },
      { rootMargin: '600px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore]);

  if (isLoading && videos.length === 0) {
    return (
      <div className={cn('grid', GRID_CLASSES[variant])}>
        {Array.from({ length: variant === 'shorts' ? 12 : 8 }).map((_, i) => (
          <VideoCardSkeleton key={i} variant={variant} />
        ))}
      </div>
    );
  }

  if (!isLoading && videos.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-12 px-8 text-center">
          <p className="text-muted-foreground max-w-sm mx-auto">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className={cn('grid', GRID_CLASSES[variant])}>
        {videos.map((video) => (
          <VideoCard
            key={video.id}
            video={video}
            reported={isReported?.(video)}
            variant={variant}
          />
        ))}
      </div>
      {hasMore && (
        <div ref={sentinelRef} className="h-4" aria-hidden />
      )}
      {isLoadingMore && (
        <div className={cn('mt-8 grid', GRID_CLASSES[variant])}>
          {Array.from({ length: 4 }).map((_, i) => (
            <VideoCardSkeleton key={i} variant={variant} />
          ))}
        </div>
      )}
    </>
  );
}
